/** Desk modules — each with restrained physical character, all reusing
 *  existing Archive services. No duplicate Oracle/Dispatch implementations. */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Radio, Search, StickyNote, FolderOpen, Inbox as InboxIcon, Trash2, Plus } from "lucide-react";
import { desk, fontMicro } from "../components/member-desk/tokens";
import { listNotes, createNote, updateNote, deleteNote, getWall } from "./api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tok = () => localStorage.getItem("archive_member_token");

export const ModuleShell = ({ id, title, icon: Icon, accent, children }) => (
  <section data-testid={`desk-module-${id}`}
    className="border bg-[#0F0E12] relative overflow-hidden"
    style={{ borderColor: desk.edge, borderTop: `2px solid ${accent}` }}>
    <header className="flex items-center gap-2 px-4 pt-3 pb-2">
      <Icon size={12} style={{ color: accent }} />
      <span className={fontMicro} style={{ color: desk.inkDim }}>{title}</span>
    </header>
    <div className="px-4 pb-4">{children}</div>
  </section>
);

export const DispatchStation = () => {
  const [inbox, setInbox] = useState(null);
  useEffect(() => {
    axios.get(`${API}/dispatch/inbox?limit=3`, { headers: { Authorization: `Bearer ${tok()}` } })
      .then((r) => setInbox(r.data)).catch(() => setInbox({ threads: [] }));
  }, []);
  const threads = inbox?.threads || [];
  return (
    <ModuleShell id="dispatch" title="Dispatch Station" icon={Radio} accent="#5DD4B3">
      {threads.length === 0 ? (
        <p className="text-[11px]" style={{ color: desk.inkMuted }}>No dispatches on the wire.</p>
      ) : threads.map((t) => (
        <Link key={t.id} to="/inbox" data-testid={`desk-dispatch-thread-${t.id}`}
          className="flex items-center gap-2 py-1.5 border-b last:border-0" style={{ borderColor: desk.edge }}>
          <span className="font-mono text-[10px] shrink-0" style={{ color: desk.gold }}>
            {(t.participants?.[0]?.callsign) || "?"}</span>
          {t.unread_count > 0 && <span className="text-[9px] font-mono px-1 border" style={{ color: desk.redact, borderColor: desk.redact }}>{t.unread_count}</span>}
          <span className="text-[11px] truncate" style={{ color: desk.inkDim }}>{t.last_message_preview || t.subject || "—"}</span>
        </Link>
      ))}
      <Link to="/inbox" data-testid="desk-open-dispatch"
        className={`${fontMicro} inline-block mt-2`} style={{ color: desk.go }}>Open Dispatch →</Link>
    </ModuleShell>
  );
};

export const FieldNotes = () => {
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const load = () => listNotes().then((r) => setNotes(r.notes || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const add = async () => {
    if (!draft.trim()) return;
    try { await createNote(draft.trim()); setDraft(""); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Note failed"); }
  };
  return (
    <ModuleShell id="field_notes" title="Field Notes" icon={StickyNote} accent="#CBB892">
      <div className="space-y-2">
        {notes.slice(0, 4).map((n) => (
          <div key={n.id} data-testid={`field-note-${n.id}`}
            className="p-2 text-[11px] leading-relaxed relative group"
            style={{ background: "#141210", border: `1px solid ${desk.edge}`, color: desk.ink }}>
            <NoteEditor note={n} onSaved={load} />
            <button onClick={() => deleteNote(n.id).then(load)} data-testid={`field-note-delete-${n.id}`}
              className="absolute top-1 right-1 opacity-0 group-hover:opacity-100" style={{ color: desk.inkMuted }}>
              <Trash2 size={11} /></button>
          </div>
        ))}
        {notes.length === 0 && <p className="text-[11px]" style={{ color: desk.inkMuted }}>Notebook is empty. Record what you find.</p>}
        <div className="flex gap-1">
          <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} maxLength={2000}
            placeholder="New field note…" data-testid="field-note-input"
            className="flex-1 bg-black/40 text-[11px] p-2 outline-none resize-none"
            style={{ border: `1px solid ${desk.edge}`, color: desk.ink }} />
          <button onClick={add} disabled={!draft.trim()} data-testid="field-note-add"
            className="px-2 border disabled:opacity-30" style={{ borderColor: desk.edgeHi, color: desk.inkDim }}>
            <Plus size={12} /></button>
        </div>
      </div>
    </ModuleShell>
  );
};

const NoteEditor = ({ note, onSaved }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(note.body);
  if (!editing) return (
    <div onClick={() => setEditing(true)} className="cursor-text whitespace-pre-wrap pr-4">{note.body}</div>
  );
  return (
    <div>
      <textarea value={val} onChange={(e) => setVal(e.target.value)} rows={3} maxLength={2000} autoFocus
        data-testid={`field-note-edit-${note.id}`}
        className="w-full bg-black/40 text-[11px] p-1 outline-none resize-none"
        style={{ border: `1px solid ${desk.edgeHi}`, color: desk.ink }} />
      <button onClick={() => { updateNote(note.id, val.trim() || note.body).then(() => { setEditing(false); onSaved(); }); }}
        data-testid={`field-note-save-${note.id}`}
        className={fontMicro} style={{ color: desk.go }}>Save</button>
    </div>
  );
};

export const ArchiveSearch = () => {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <ModuleShell id="search" title="Archive Search" icon={Search} accent="#9FA8D4">
      <form onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav(`/archive?q=${encodeURIComponent(q.trim())}`); }}
        className="flex gap-1">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dossiers, people, files…"
          data-testid="desk-search-input"
          className="flex-1 bg-black/40 text-[12px] px-2 py-2 outline-none font-mono"
          style={{ border: `1px solid ${desk.edge}`, color: desk.ink }} />
        <button type="submit" data-testid="desk-search-go" className="px-3 border"
          style={{ borderColor: desk.edgeHi, color: desk.inkDim }}><Search size={12} /></button>
      </form>
      <p className="text-[10px] mt-2" style={{ color: desk.inkMuted }}>Direct index into the full Archive.</p>
    </ModuleShell>
  );
};

export const EvidenceTray = () => {
  const [pins, setPins] = useState(null);
  useEffect(() => {
    getWall().then((r) => setPins((r.pins || []).filter((p) => p.type === "evidence" || p.type === "timeline").slice(0, 4)))
      .catch(() => setPins([]));
  }, []);
  return (
    <ModuleShell id="evidence_tray" title="Evidence Tray" icon={FolderOpen} accent="#E0A526">
      {!pins || pins.length === 0 ? (
        <p className="text-[11px]" style={{ color: desk.inkMuted }}>
          Tray is empty. Pin evidence or timeline events from the <Link to="/epstein-files" className="underline" style={{ color: desk.inkDim }}>Files</Link> as you investigate.
        </p>
      ) : pins.map((p) => (
        <Link key={p.id} to={p.route || "#"} data-testid={`evidence-tray-${p.id}`}
          className="block py-1.5 border-b last:border-0 text-[11px]" style={{ borderColor: desk.edge, color: desk.ink }}>
          <span className="font-mono text-[9px] uppercase mr-2" style={{ color: desk.gold }}>{p.subtitle || p.type}</span>
          {p.title}
        </Link>
      ))}
    </ModuleShell>
  );
};

export const RecentActivity = ({ items }) => (
  <ModuleShell id="recent_activity" title="Recent Activity" icon={InboxIcon} accent="#7B7168">
    {(items || []).length === 0 ? (
      <p className="text-[11px]" style={{ color: desk.inkMuted }}>No recent trail. Open a dossier to begin.</p>
    ) : (items || []).slice(0, 5).map((t) => (
      <Link key={t.slug} to={`/topic/${t.slug}`} className="block py-1 text-[11px] truncate"
        style={{ color: desk.inkDim }} data-testid={`recent-activity-${t.slug}`}>
        · {t.title}
      </Link>
    ))}
  </ModuleShell>
);
